// Distance markers along a line: a chevron pointing in the direction of travel,
// labelled "1 km", "2 km", … on whole units. Shared by the hike map and the
// recording map so both draw them the same way.

import { bearing as turfBearing, distance as turfDistance } from '@turf/turf';
import { BASE_STEP_UNITS, markerTierForStep, iconOpacityZoomExpression } from './distanceMarkerTiers';

const KM_PER_MILE = 1.609344;

function linesOf(geometry) {
  if (geometry?.type === 'LineString') return [geometry.coordinates];
  if (geometry?.type === 'MultiLineString') return geometry.coordinates;
  return [];
}

/**
 * Markers along the combined geometry of `features`, counting on from one feature to the next.
 * Emitted on a 0.05-unit base grid, keeping only steps that land on a display tier (0.1 / 0.25 /
 * 0.5 unit), each flagged with the grids it belongs to so the layer can fade tiers in with zoom.
 */
export function distanceMarkersAlong(features, { isMetric }) {
  const unitLabel = isMetric ? 'km' : 'mi';
  const kmPerUnit = isMetric ? 1 : KM_PER_MILE;
  const out = [];
  let cumulativeKm = 0;
  let nextStep = 1;
  features.forEach((feature) => {
    linesOf(feature?.geometry).forEach((coords) => {
      if (!Array.isArray(coords) || coords.length < 2) return;
      // Walk segment-by-segment so each marker is O(1) to place.
      // turfAlong scans from the line start on every call, which was
      // O(n²) for hikes stored as a single massive coordinate array.
      let lineKm = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        const from = coords[i];
        const to = coords[i + 1];
        const segKm = turfDistance(from, to, { units: 'kilometers' });
        const segStart = cumulativeKm + lineKm;
        const segEnd = segStart + segKm;
        while ((nextStep * BASE_STEP_UNITS) * kmPerUnit <= segEnd) {
          const tier = markerTierForStep(nextStep);
          if (tier) {
            const targetKm = (nextStep * BASE_STEP_UNITS) * kmPerUnit;
            const t = segKm > 0 ? (targetKm - segStart) / segKm : 0;
            const isWhole = tier.wholeUnit !== null;
            out.push({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [from[0] + t * (to[0] - from[0]), from[1] + t * (to[1] - from[1])],
              },
              properties: {
                distance: nextStep * BASE_STEP_UNITS,
                unit: unitLabel,
                label: isWhole ? `${tier.wholeUnit} ${unitLabel}` : '',
                isWhole,
                bearing: turfBearing([from[0], from[1]], [to[0], to[1]]),
                onHalf: tier.onHalf ? 1 : 0,
                onQuarter: tier.onQuarter ? 1 : 0,
                onTenth: tier.onTenth ? 1 : 0,
              },
            });
          }
          nextStep += 1;
        }
        lineKm += segKm;
      }
      cumulativeKm += lineKm;
    });
  });
  return out;
}

/** The symbol layer for markers from {@link distanceMarkersAlong}; its source shares the layer's id. */
export function distanceMarkerLayer(id: string, visibility: 'visible' | 'none' = 'visible') {
  return {
    id,
    type: 'symbol',
    source: id,
    minzoom: 12,
    layout: {
      // The chevron sprite is drawn pointing east, so subtract 90°
      // from the geographic bearing to align it with travel direction.
      'icon-image': 'hike-arrow-icon',
      'icon-size': 0.15,
      'icon-rotate': ['-', ['get', 'bearing'], 90],
      'icon-rotation-alignment': 'map',
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'text-field': ['get', 'label'],
      'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      'text-size': 11,
      'text-anchor': 'left',
      'text-offset': [0.9, 0],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'symbol-sort-key': ['get', 'distance'],
      visibility,
    },
    paint: {
      // Zoom-reactive density: show one regular grid per zoom band by
      // using the active band's per-feature flag as the icon opacity.
      'icon-opacity': iconOpacityZoomExpression(),
      'text-color': '#1f2026',
      'text-halo-color': 'rgba(255,255,255,0.95)',
      'text-halo-width': 1.5,
      'text-halo-blur': 0.5,
    },
  };
}
